<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="650px" :persistent="persistent">
      <v-card dense>
        <v-toolbar elevation="0">
          <v-toolbar-title
            class="text--subtitle error--text"
            style="padding-left: 10px"
          >
            <v-icon color="error" style="padding-bottom: 2px">mdi-lock</v-icon>
            {{
              $t(
                'message.pages.myAccount.modals.title.changeEmail'
              ).toUpperCase()
            }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              :disabled="isFetching"
              icon
              @click="handleClose()"
              v-show="!persistent"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="px-7 py-0">
          <v-row no-gutters v-if="!doneState && !doneStateForgetSession">
            <v-col cols="12" v-if="!confirmLogoutSessions">
              {{
                $t('message.pages.myAccount.modals.description.changeEmail1')
              }}
              <br />
              {{
                $t('message.pages.myAccount.modals.description.changeEmail2')
              }}
            </v-col>
            <v-col cols="12" v-else class="mb-3">
              <span
                v-html="
                  $t(
                    'message.pages.myAccount.modals.description.logoutSessions'
                  )
                "
              ></span>
            </v-col>
            <v-col cols="12" class="my-3" v-if="!confirmLogoutSessions">
              <b>{{
                $t('message.pages.video.deleteModal.confirmRemoveMessage2')
              }}</b>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="password"
                class="pr-2"
                :label="$t('message.password')"
                dense
                outlined
                autofocus
                autocomplete="off"
                :error="
                  changeEmailError.invalidPass ||
                  changeEmailError.generalError ||
                  Boolean(changeEmailError.customErrorMessage)
                "
                :messages="changeEmailErrorMessage"
                :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPass ? 'text' : 'password'"
                @click:append="showPass = !showPass"
                @keyup.enter="handleConfirm()"
              >
                <template v-slot:message="{ message }">
                  <span
                    class="red--text caption align-text"
                    v-if="
                      changeEmailError.invalidPass ||
                      changeEmailError.generalError ||
                      Boolean(changeEmailError.customErrorMessage)
                    "
                  >
                    <v-icon color="red" size="18" class="pb-1">
                      mdi-alert-circle
                    </v-icon>
                    {{ message }}
                  </span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" v-if="!confirmLogoutSessions">
              <v-checkbox
                v-model="checkboxConfirmation"
                class="mt-0 pt-0"
                hide-details
              >
                <template v-slot:label>
                  <span style="font-size: 14px">{{
                    $t('message.pages.video.deleteModal.checkboxText')
                  }}</span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <v-row v-else-if="doneStateForgetSession" no-gutters class="mb-4">
            <v-col cols="12">
              {{
                $t(
                  'message.pages.myAccount.modals.description.doneLogoutSessions'
                )
              }}
            </v-col>
          </v-row>
          <v-row no-gutters v-else class="mb-4">
            <v-col cols="12">
              {{
                $t(
                  'message.pages.myAccount.modals.description.confirmChangeEmail'
                )
              }}
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-7 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            class="px-6"
            text
            @click="handleClose"
            :disabled="isFetching"
            v-if="!doneState && !persistent && !doneStateForgetSession"
          >
            {{ $t('message.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="disabledConfirm"
            :loading="isFetching"
            @click="handleConfirm"
          >
            <span class="px-5">{{ $t('message.confirm') }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
export default {
  name: 'ConfirmChangeEmail',
  components: {},
  props: {
    openConfirm: {
      type: Boolean,
      required: true,
      default: false,
    },
    confirmLogoutSessions: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFetching: {
      type: Boolean,
      required: true,
      default: false,
    },
    changeEmailError: {
      type: Object,
      required: true,
    },
    doneState: {
      type: Boolean,
      required: true,
      default: false,
    },
    doneStateForgetSession: {
      type: Boolean,
      required: false,
      default: false,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    checkboxConfirmation: false,
    showPass: false,
    password: '',
  }),
  methods: {
    handleClose() {
      this.password = ''
      this.showPass = false
      this.checkboxConfirmation = false
      this.$emit('close')
    },
    handleConfirm() {
      if (this.disabledConfirm) return
      if (this.doneState || this.doneStateForgetSession) {
        this.handleClose()
      } else {
        this.$emit('confirm', this.password)
      }
    },
  },
  computed: {
    disabledConfirm() {
      if (this.confirmLogoutSessions) {
        if (!this.password || this.isFetching) {
          return true
        }
        return false
      }
      if (
        (this.isFetching || !this.checkboxConfirmation || !this.password) &&
        !this.doneState &&
        !this.doneStateForgetSession
      ) {
        return true
      }

      return false
    },
    open: {
      get() {
        if (!this.openConfirm) this.handleClose()
        return this.openConfirm
      },
      set(newValue) {
        if (!newValue) this.handleClose()
      },
    },
    changeEmailErrorMessage() {
      const i18n = this.$i18next
      if (this.changeEmailError.invalidPass) {
        return i18n.t('message.invalidPassword')
      }
      if (this.changeEmailError.generalError) {
        return i18n.t('message.errors.unexpectedError')
      }
      if (this.changeEmailError.customErrorMessage) {
        return i18n.t(
          `message.pages.myAccount.errors.${this.changeEmailError.customErrorMessage}`
        )
      }
      return false
    },
  },
}
</script>
